/**
 * Client-side functions for profile API
 */

import type { Profile, MembershipTier } from "@/types/profile";
import { getApiUrl } from "./utils";
import { authFetch } from "./auth-fetch";

type GetProfilesOptions = {
  excludeSyncRelationships?: boolean;
};

const PROFILE_CACHE_TTL_MS = 60_000;
const PROFILE_NULL_CACHE_TTL_MS = 10_000;
const profileCache = new Map<string, { value: Profile | null; expiresAt: number }>();
const profileInFlight = new Map<string, Promise<Profile | null>>();

/** Clears in-memory profile fetch cache (e.g. after forced sign-out for deleted account). */
export function clearProfileMemoryCache() {
  profileCache.clear();
  profileInFlight.clear();
}

export async function getProfiles(limit?: number, options?: GetProfilesOptions): Promise<Profile[]> {
  const params = new URLSearchParams();
  if (limit) params.append("limit", limit.toString());
  if (options?.excludeSyncRelationships) {
    params.append("excludeSyncRelationships", "1");
  }
  const apiUrl = getApiUrl(`/api/profiles?${params.toString()}`);

  try {
    // Use authFetch so API receives session (bearer token on mobile, cookies on desktop)
    // This ensures getCurrentUserForServer can exclude the current user from results
    const response = await authFetch(apiUrl, {
      credentials: "include", // Ensure cookies are sent for authentication
    });
    if (!response.ok) {
      const text = await response.text();
      let errorData: any = {};
      try {
        const ct = response.headers.get("Content-Type") || "";
        if (ct.includes("application/json")) errorData = JSON.parse(text);
      } catch {
        errorData = { error: `HTTP ${response.status}` };
      }
      const errorMessage = errorData.error || `Failed to fetch profiles: ${response.status} ${response.statusText}`;
      console.error("[getProfiles] [DEV] Profiles API error:", {
        errorMessage,
        errorData,
        status: response.status,
        statusText: response.statusText,
      });
      
      // If it's a server error (500), return empty array instead of throwing
      if (response.status >= 500) {
        console.warn("[getProfiles] [DEV] Server error fetching profiles, returning empty array");
        return [];
      }
      
      throw new Error(errorMessage);
    }

    const text = await response.text();
    const ct = response.headers.get("Content-Type") || "";
    if (!ct.includes("application/json")) {
      console.error("[getProfiles] API returned HTML instead of JSON:", text.slice(0, 120));
      throw new Error(
        `Profiles API returned ${ct || "non-JSON"}. Check that /api/profiles exists and returns JSON.`
      );
    }
    let data: unknown;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error("Profiles API returned invalid JSON.");
    }
    const transformed = Array.isArray(data) ? data.map(transformProfile) : [];
    return transformed;
  } catch (error: any) {
    console.error("[getProfiles] [DEV] Error fetching profiles", {
      error: error?.message,
      errorName: error?.name,
      stack: error?.stack,
      rawError: error,
      errorType: typeof error,
      errorKeys: error && typeof error === "object" ? Object.keys(error) : [],
    });
    
    // Handle network errors (Failed to fetch)
    if (error?.message === "Failed to fetch" || error?.name === "TypeError") {
      console.warn("[getProfiles] [DEV] Network error fetching profiles (possibly offline or API unavailable), returning empty array");
      return [];
    }
    // Handle timeout errors gracefully - return empty array so UI can show retry option
    if (error?.message?.includes?.("timed out") || error?.message?.includes?.("timeout")) {
      console.warn("[getProfiles] [DEV] Request timed out - returning empty array");
      return [];
    }
    // Handle abort errors (timeout)
    if (error?.name === "AbortError" || error?.message?.toLowerCase?.().includes?.("aborted")) {
      console.warn("[getProfiles] [DEV] Request aborted (likely timeout) - returning empty array");
      return [];
    }
    // Re-throw other errors
    throw error;
  }
}

/**
 * Batch-load profiles for matched users (single HTTP request). Results are merged into
 * the profile store by callers; also use seedProfileCacheFromProfiles to populate getProfile cache.
 */
export async function getProfilesForMatchedAuthUsers(
  userIds: string[],
): Promise<Profile[]> {
  const unique = [...new Set(userIds.map((id) => id.trim()).filter(Boolean))];
  if (unique.length === 0) return [];

  const response = await authFetch(getApiUrl("/api/profiles/for-matches"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ userIds: unique }),
  });

  if (!response.ok) {
    return [];
  }

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    return [];
  }

  const raw = (data as { profiles?: unknown })?.profiles;
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => transformProfile(row));
}

/** Warm the in-memory getProfile cache after a batch load (avoids duplicate /api/profiles/:id). */
export function seedProfileCacheFromProfiles(profiles: Profile[]) {
  const now = Date.now();
  for (const p of profiles) {
    if (!p.userId) continue;
    const key = p.userId.trim();
    profileCache.set(key, {
      value: p,
      expiresAt: now + PROFILE_CACHE_TTL_MS,
    });
  }
}

export async function getProfile(userId: string, options?: { forceRefresh?: boolean }): Promise<Profile | null> {
  // Validate userId before making the request
  if (!userId || userId === "temp" || userId.trim() === "") {
    console.warn("[getProfile] Called with invalid userId:", userId);
    return null;
  }

  const cacheKey = userId.trim();
  const now = Date.now();
  const forceRefresh = options?.forceRefresh === true;

  if (!forceRefresh) {
    const cached = profileCache.get(cacheKey);
    if (cached && cached.expiresAt > now) {
      return cached.value;
    }

    const pending = profileInFlight.get(cacheKey);
    if (pending) {
      return pending;
    }
  }

  const requestPromise = (async (): Promise<Profile | null> => {
    try {
      const { authFetch } = await import("./auth-fetch");
      const response = await authFetch(`/api/profiles/${cacheKey}`, {
        credentials: "include",
      });

      if (!response.ok) {
        // Common transient/auth statuses should fail softly.
        if (response.status === 401 || response.status === 404 || response.status === 503 || response.status >= 500) {
          return null;
        }
        return null;
      }

      const data = await response.json();
      return transformProfile(data);
    } catch (error) {
      if (
        (error instanceof Error && (error.message.includes("timed out") || error.name === "AbortError" || error.message.includes("aborted"))) ||
        (error instanceof TypeError && error.message.includes("fetch"))
      ) {
        return null;
      }
      throw error;
    }
  })();

  profileInFlight.set(cacheKey, requestPromise);

  try {
    const profile = await requestPromise;
    profileCache.set(cacheKey, {
      value: profile,
      expiresAt: Date.now() + (profile ? PROFILE_CACHE_TTL_MS : PROFILE_NULL_CACHE_TTL_MS),
    });
    return profile;
  } finally {
    profileInFlight.delete(cacheKey);
  }
}

export async function createProfile(profile: Partial<Profile>): Promise<Profile> {
  const response = await fetch(getApiUrl("/api/profiles"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include", // Ensure cookies are sent for authentication
    body: JSON.stringify(profile),
  });

  if (!response.ok) {
    throw new Error("Failed to create profile");
  }

  const data = await response.json();
  return transformProfile(data);
}

export async function updateProfile(userId: string, updates: Partial<Profile>): Promise<Profile> {
  // Validate userId is not "temp" or empty
  if (!userId || userId === "temp" || userId.trim() === "") {
    throw new Error("Invalid userId: Cannot update profile without a valid user ID");
  }

  const response = await authFetch(getApiUrl(`/api/profiles/${userId}`), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include", // Ensure cookies are sent for authentication
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    // Check status BEFORE trying to parse response
    const status = response.status;
    
    // If it's a 405, this might be a Next.js routing issue - check if profile was actually updated
    if (status === 405) {
      console.warn(`Profile update returned 405 for userId: ${userId}. This may be a Next.js routing issue. Checking if profile was updated...`);
      // Try to fetch the profile to see if it was actually updated
      try {
        const getResponse = await fetch(`/api/profiles/${userId}`, {
          credentials: "include", // Ensure cookies are sent for authentication
        });
        if (getResponse.ok) {
          const currentProfile = await getResponse.json();
          console.log("Profile appears to have been updated despite 405 error. Returning updated profile.");
          return transformProfile(currentProfile);
        }
      } catch (fetchError) {
        console.warn("Could not verify if profile was updated:", fetchError);
        // Fall through to throw the error
      }
    }
    
    // Try to parse error response, but handle cases where response body might be empty
    let errorData: any = {};
    try {
      // Clone the response so we can read it multiple times if needed
      const responseClone = response.clone();
      const text = await responseClone.text();
      if (text) {
        try {
          errorData = JSON.parse(text);
        } catch (parseError) {
          // If it's not JSON, just use the text as the error message
          errorData = { error: text || `HTTP ${status}` };
        }
      }
    } catch (readError) {
      // If we can't read the response, just use the status
      console.warn("Could not read error response:", readError);
      errorData = { error: `HTTP ${status}` };
    }
    
    const details = errorData.details;
    const detailsStr = Array.isArray(details) ? details.join(". ") : (details ? String(details) : "");
    const errorMessage = errorData.error || `Failed to update profile: ${status} ${response.statusText}`;
    const fullMessage = detailsStr ? `${errorMessage}: ${detailsStr}` : errorMessage;
    console.error("Profile update API error:", fullMessage, errorData);
    throw new Error(fullMessage);
  }

  const data = await response.json();
  // Bust the in-memory cache so the next getProfile() call fetches fresh data from
  // the server. Without this, a Capacitor app-resume (which triggers fetchCurrentProfile
  // within 100ms via onRehydrateStorage) would return the stale pre-update profile,
  // causing the UI to revert to the old photo.
  profileCache.delete(userId.trim());
  return transformProfile(data);
}

function normalizeProfilePromptsRaw(raw: unknown): Profile["profilePrompts"] {
  if (raw == null) return [];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((p): p is Record<string, unknown> => p != null && typeof p === "object")
    .map((p) => ({
      question: String(p.question ?? "").trim(),
      answer: String(p.answer ?? "").trim(),
    }))
    .filter((p) => p.question.length > 0);
}

function transformProfile(dbProfile: any): Profile {
  // CRITICAL: Preserve account type - check both account_type and user_role
  // NEVER overwrite admin with user
  // NEVER default to "user" - preserve what's in the database or return null
  const accountType = dbProfile.accountType || dbProfile.account_type;
  const userRole = dbProfile.user_role || dbProfile.userRole || dbProfile.account_type;
  const isAdmin = accountType === "admin" || userRole === "admin";
  
  // CRITICAL: If we can't determine the role from the database, don't guess
  // Return null for accountType/user_role rather than defaulting to "user"
  // This prevents phantom "user" profiles from getting admin access
  
  // CRITICAL: Log onboarding_completed transformation (always log for tomcavies@gmail.com)
  const userEmail = dbProfile.email || "unknown";
  const isTomCavies = userEmail.toLowerCase() === "tomcavies@gmail.com";
  
  if (process.env.NODE_ENV === "development" || isTomCavies) {
    console.log("[Profile Transform] [VERIFICATION FIX] Transforming profile:", {
      source: "src/lib/api/profiles-client.ts transformProfile",
      profileId: dbProfile.id,
      userId: dbProfile.userId || dbProfile.user_id,
      email: dbProfile.email,
      accountType,
      userRole,
      isAdmin,
      rawAccountType: dbProfile.account_type,
      rawUserRole: dbProfile.user_role,
      // CRITICAL: Check onboarding_completed values from database
      rawOnboardingCompleted: dbProfile.onboarding_completed,
      rawOnboardingCompletedType: typeof dbProfile.onboarding_completed,
      camelOnboardingCompleted: dbProfile.onboardingCompleted,
      camelOnboardingCompletedType: typeof dbProfile.onboardingCompleted,
      // Will be set to:
      willBeOnboardingCompleted: dbProfile.onboardingCompleted ?? dbProfile.onboarding_completed ?? false,
    });
  }
  
  return {
    id: dbProfile.id,
    userId: dbProfile.userId || dbProfile.user_id || dbProfile.id,
    fullName: dbProfile.fullName || dbProfile.full_name,
    headline: dbProfile.headline,
    role: dbProfile.role,
    businessStage: dbProfile.businessStage || dbProfile.business_stage,
    teamSize: dbProfile.teamSize || dbProfile.team_size,
    industries: dbProfile.industries || [],
    lookingFor: dbProfile.lookingFor || dbProfile.looking_for || [],
    servicesOffered: dbProfile.servicesOffered || dbProfile.services_offered || [],
    bio: dbProfile.bio || "",
    profilePrompts: normalizeProfilePromptsRaw(dbProfile.profilePrompts ?? dbProfile.profile_prompts),
    websiteUrl: dbProfile.websiteUrl || dbProfile.website_url,
    linkedinUrl: dbProfile.linkedinUrl || dbProfile.linkedin_url,
    instagramHandle: dbProfile.instagramHandle || dbProfile.instagram_handle,
    city: dbProfile.city,
    country: dbProfile.country,
    countryCode: dbProfile.countryCode || dbProfile.country_code || null,
    latitude: dbProfile.latitude || dbProfile.lat || null,
    longitude: dbProfile.longitude || dbProfile.lng || dbProfile.lon || null,
    avatarUrl: dbProfile.avatarUrl || dbProfile.avatar_url,
    membershipTier: (dbProfile.membershipTier || dbProfile.membership_tier || "free").toLowerCase() as MembershipTier,
    stripeAccountId: dbProfile.stripeAccountId || dbProfile.stripe_account_id || dbProfile.stripeConnectId || dbProfile.stripe_connect_id,
    stripeConnectId: dbProfile.stripeConnectId || dbProfile.stripe_connect_id || dbProfile.stripeAccountId || dbProfile.stripe_account_id,
    totalXp: dbProfile.totalXp ?? dbProfile.total_xp ?? 0,
    weeklyXp: dbProfile.weeklyXp ?? dbProfile.weekly_xp ?? 0,
    level: dbProfile.level ?? 1,
    latestBadge: dbProfile.latestBadge ?? dbProfile.latest_badge ?? null,
    matchesCount: dbProfile.matchesCount || dbProfile.matches_count || 0,
    eventsAttendedCount: dbProfile.eventsAttendedCount || dbProfile.events_attended_count || 0,
    communityPostsCount: dbProfile.communityPostsCount || dbProfile.community_posts_count || 0,
    // CRITICAL: Preserve admin status - NEVER overwrite admin with user
    // If the profile is admin in the database, it MUST remain admin
    // Never default to "user" if we can't determine the role - preserve what's in DB
    accountType: isAdmin ? "admin" : (accountType || null),
    user_role: isAdmin ? "admin" : (userRole || null),
    onboardingCompleted: dbProfile.onboardingCompleted ?? dbProfile.onboarding_completed ?? false,
    dateOfBirth: dbProfile.dateOfBirth || dbProfile.date_of_birth || null,
    ageVerified: dbProfile.ageVerified ?? dbProfile.age_verified ?? false,
    emailVerified: dbProfile.emailVerified ?? dbProfile.email_verified ?? false,
    isVerifiedFromDb:
      typeof dbProfile.isVerifiedFromDb === "boolean"
        ? dbProfile.isVerifiedFromDb
        : dbProfile.is_verified === true,
    phoneNumber: dbProfile.phoneNumber ?? dbProfile.phone_number ?? null,
    timezone: dbProfile.timezone ?? dbProfile.timeZone ?? "UTC",
    createdAt: dbProfile.createdAt || dbProfile.created_at,
    updatedAt: dbProfile.updatedAt || dbProfile.updated_at,
    tutorialCompletedAt: dbProfile.tutorialCompletedAt ?? dbProfile.tutorial_completed_at ?? null,
    marketingOptIn: dbProfile.marketingOptIn ?? dbProfile.marketing_opt_in ?? false,
    businesses: (dbProfile.businesses || []).map((b: any) => ({
      id: b.id,
      name: b.name,
      role: b.role,
      isPrimary: b.isPrimary ?? b.is_primary ?? false,
      businessId: b.businessId ?? b.business_id,
    })),
  };
}
